import Vue from "vue";
import {
    BillingHistoryState,
    BillingHistoryMonth,
    BillingHistoryYearSummary
} from "@/employer/types";
import { FetchState } from "@/shared/types";
import * as _ from "lodash";
import * as billingService from "@/shared/api/billingService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";

const initialState: BillingHistoryState = {
    cognitoUsername: null,
    yearSummaryDictionary: {},
    years: [],
    yearsFetchState: FetchState.Uninitialized,
    selectedYear: null
};

export const useBillingHistoryStore = defineStore("billingHistory", {
    state: (): BillingHistoryState => (_.cloneDeep(initialState)),
    getters: {
        yearsLoaded: state => state.yearsFetchState === FetchState.Loaded,
        monthSummaries: state => year => state.yearSummaryDictionary[year].months,
        yearIsLoaded: state => year => state.yearSummaryDictionary[year]
            && state.yearSummaryDictionary[year].fetchState === FetchState.Loaded,
        noBillingData: state => state.yearsFetchState === FetchState.Loaded && state.years.length === 0
    },
    actions: {
        setYearsFetchState (fetchState: FetchState) {
            this.yearsFetchState = fetchState;
        },
        setYears (years: number[]) {
            this.years = years.sort();
            _.forEach(years, year => {
                const result = this.yearSummaryDictionary[year];
                if (!result) {
                    const entry: BillingHistoryYearSummary = {
                        year,
                        fetchState: FetchState.Uninitialized,
                        months: []
                    };
                    Vue.set(this.yearSummaryDictionary, year, entry);
                }
            });
        },
        setMonthSummaries (args: { year: number, months: BillingHistoryMonth[] }): void {
            const yearRecord = this.yearSummaryDictionary[args.year];
            yearRecord.months = args.months;
        },
        setMonthSummaryFetchState (args: { year: number, fetchState: FetchState }) {
            const yearRecord = this.yearSummaryDictionary[args.year];
            yearRecord.fetchState = args.fetchState;
        },
        setSelectedYear (year: number) {
            this.selectedYear = year;
        },
        setUser (username: string) {
            this.cognitoUsername = username;
            this.selectedYear = null;
            this.yearSummaryDictionary = {};
            this.years = [];
            this.yearsFetchState = FetchState.Uninitialized;
        },
        loadYears (args: { username: string, partnerId: number }) {
            if (this.cognitoUsername !== args.username) {
                this.setUser(args.username);
            }

            if (this.yearsFetchState !== FetchState.Uninitialized) {
                return;
            }

            this.setYearsFetchState(FetchState.Loading);
            return billingService.getBillingHistoryYears(args.username, args.partnerId)
                .then(response => {
                    if (!response || !response.data) {
                        this.setYearsFetchState(FetchState.Uninitialized);
                        return;
                    }

                    this.setYears(response.data);
                    this.setYearsFetchState(FetchState.Loaded);
                    if (response.data.length > 0) {
                        this.updateSelectedYear({
                            year: response.data[response.data.length - 1],
                            username: args.username,
                            partnerId: args.partnerId
                        });
                    }
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.setYearsFetchState(FetchState.Uninitialized);
                });
        },
        loadMonthSummaries (args: { year: number, username: string, partnerId: number }) {
            if (this.yearSummaryDictionary[args.year].fetchState === FetchState.Loaded) {
                return;
            }

            this.setMonthSummaryFetchState({ year: args.year, fetchState: FetchState.Loading });
            return billingService.getBillingHistoryMonthSummaries(args.year, args.username, args.partnerId)
                .then(response => {
                    if (!response || !response.data) {
                        this.setMonthSummaryFetchState({ year: args.year, fetchState: FetchState.Uninitialized });
                        return;
                    }

                    this.setMonthSummaries({ months: response.data, year: args.year });
                    this.setMonthSummaryFetchState({ year: args.year, fetchState: FetchState.Loaded });
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        updateSelectedYear (args: { year: number, username: string, partnerId: number }) {
            this.setSelectedYear(args.year);
            this.loadMonthSummaries({ year: args.year, username: args.username, partnerId: args.partnerId });
        }
    }
});

